import {AfterViewInit, Component, forwardRef, Inject, OnInit, ViewChild} from '@angular/core';
import { StateMap, UnitOfWork, UserManager } from '../../services/common';
import { EditManager, IEditHost, UtilFns } from '../../utils/common';
import { STAFF_ROUTES } from './staff.routes';

import {Material, MaterialComponent, MaterialComposition} from '../../entities/EntityModels';
import {MaterialSelectorComponent} from '../material/material-selector.component';
import {NCSEditData, NCSViewerComponent} from './ncs-viewer.component';

@Component({
    selector: 'ncs-editor',
    templateUrl: './ncs-editor.html',
})
export class NCSEditorComponent implements OnInit, AfterViewInit {
    @ViewChild(MaterialSelectorComponent, { static: true }) _materialSelectorComponent: MaterialSelectorComponent;

    _edm: EditManager<MaterialComposition>;
    _edmComponents: EditManager<MaterialComponent>;

    _materials: Material[];

    public _isLoading = false;
    public _pageTitleComposition: string;
    public _pageTitleConstituent: string;
    public _componentAmountValidationMessage: string;
    public _dateValidationMessage: string;
    public _materialValidationMessage: string;
    public _validationMessage: string;

    public _componentMaterialChanged: boolean;
    public _pickValidationDate = '';
    public _selectedMaterial: Material;
    public _selectedComponentMaterial: MaterialComponent;

    public _parentData: NCSEditData;

    constructor(public _stateMap: StateMap, public _uow: UnitOfWork, public _userManager: UserManager,
                @Inject(forwardRef(() => NCSViewerComponent)) public _parent: NCSViewerComponent) {
        this._parentData = _parent._data;
    }

    ngOnInit() {
        this._edm                       = new EditManager<MaterialComposition>(this, this._uow, this._stateMap, 'NCS Editor');
        this._edmComponents             = new EditManager<MaterialComponent>(this, this._uow, this._stateMap, 'NCS Editor');
        this._edmComponents.entities    = [];

        this._edm.onlyEntity    = this._parentData.entity;
        this._edm.editing       = true;

        if (this._parentData.editing && this._edm.currentEntity.validationDate != null) {
            this.pickValidationDate = this.utilFns.formatDate(this._edm.currentEntity.validationDate, 'MM-DD-yyyy');
        }

        this.formatPageTitles();
    }

    ngAfterViewInit() {
    }

    formatPageTitles() {
        const firstPart = (this._parentData.deleting) ? 'Removing Constituent ' : (this._parentData.adding) ? 'Adding Constituent' : 'Editing Constituent ';
        // tslint:disable-next-line:max-line-length

        let secondPart = '';
        if (!this._parentData.adding) {
            // tslint:disable-next-line:max-line-length
            secondPart = (this._edm.currentEntity.materialComponent.material != null) ? this._edm.currentEntity.materialComponent.material.realCASNumber + ', ' + this._edm.currentEntity.materialComponent.material.formattedRIFMId : '';
        }

        this._pageTitleConstituent = firstPart + secondPart;

        if (this.compositionMaterial != null) {
            this._pageTitleComposition = 'For NCS Material ' + this.compositionMaterial.realCASNumber + ', ' + this.compositionMaterial.formattedRIFMId;
        } else {
            this._pageTitleComposition = '';
        }
    }

    canDeactivate() {
        return (!this._uow.hasChanges());
    }

    get utilFns() {
        return UtilFns;
    }

    canApply() {
        if (this._edm.editing) {
            if (this._edm.currentEntity.materialComponent == null) {
                return false;
            }
            return (this._uow.hasChanges() && !this._edm.currentEntity.entityAspect.hasValidationErrors);
        }
        return false;
    }

    canCancel() {
        return this._uow.hasChanges();
    }

    canDelete() {
        return (this._parentData.deleting && !this._parentData.deleteFinished);
    }

    canSave() {
        return this._uow.hasChanges();
    }

    canShowBack() {
        return (!this._uow.hasChanges());
    }

    showSaveButton(): boolean {
        if (this._edm.editing || this._parentData.deleting) { return false; }

        return (this._parentData.adding || this._parentData.editing);
    }

    onApply() {
        this.validateChanges().then(r => {
           if (r) {
               const appliedComponents     = this._edmComponents.onApplyCore();
               const appliedCompositions   = this._edm.onApplyCore();
               return (appliedComponents && appliedCompositions);
           }
        });
        return false;
    }

    public validateChanges(): Promise<boolean> {
        this._componentAmountValidationMessage = '';
        this._dateValidationMessage = '';
        this._materialValidationMessage = '';

        if (this._parentData.deleting) { return Promise.resolve(true); }

        // validate component amount
        if (this._edm.currentEntity.componentAmount <= 0) {
            this._componentAmountValidationMessage = 'The Constituent Percentage should be greater than zero.';
            return Promise.resolve(false);
        }

        // validate validation date
        if (this.isValidString(this.pickValidationDate)) {
            if (!this.isValidDate(this.pickValidationDate)) {
                this._dateValidationMessage = 'The Validation Date value is not a valid date.';
                return Promise.resolve(false);;
            }
        }

        // make sure a new component material is not already assigned to a composition
        if (!this._componentMaterialChanged) { return Promise.resolve(true); }

        return this.compositionAlreadyExists(this._edm.currentEntity.materialId, this._selectedMaterial.materialId).then(r => {
            if (r) {
                this._materialValidationMessage = 'This Constituent is already assigned to the NCS Material.';
                return (false);
            } else {
                return (true);
            }
        });
    }

    public onBack() {
        this._parentData.adding         = false;
        this._parentData.deleteFinished = false;
        this._parentData.deleting       = false;
        this._parentData.editing        = false;
        this._parentData.entity         = null;
        this._parentData.inSubEditor    = false;
    }

    public onCancel() {
        this._edm.onCancelCore();
        this._edmComponents.onCancelCore();
        this.onBack();
    }

    onDelete() {
        this._edm.currentEntity.entityAspect.setDeleted();
        this._edmComponents.onApplyCore();

        this.onSave();
    }

    onSave() {
        return this._edm.onSaveCore().then(() => {
            this._parentData.adding             = false;
            this._parentData.editing            = false;
            this._parentData.deleteFinished     = true;

            this._validationMessage = (this._parentData.deleting) ? 'Removal successful.' : 'Changes were successfully saved.';

            this._parent.refreshMaterialComponentData();

        }).catch((e) => {
            this._validationMessage = 'An error occurred: ' + e.toString();
        });
    }

    // ************************************************************************
    // *Component Data
    // ************************************************************************
    fetchMaterialComponent(materialId: number) {
        this._selectedComponentMaterial = null;
        const params = {
            materialId: materialId
        };
        return this._uow.fetch('Materials/MaterialComponents', params).then(r => {
            if (r != null && r.length > 0) { // the result set can be empty if this is the first time a material is assigned to a composition.
                this._selectedComponentMaterial = r[0];
            }
        });
    }

    async compositionAlreadyExists(compositionMaterialId: number, componentMaterialId: number): Promise<boolean> {
        const params = {compositionMaterialId: compositionMaterialId, componentMaterialId: componentMaterialId};
        return this._uow.fetch('Materials/MaterialCompositionByRelationship', params).then(r => {
            return (r != null && r.length > 0);
        });
    }

    // ************************************************************************
    // *Material Data
    // ************************************************************************
    get compositionMaterial(): Material {
        if (this._edm.currentEntity == null) {
            return null;
        }

        return (this._edm.currentEntity.material);
    }

    public get pickValidationDate(): string {
        return this._pickValidationDate;
    }

    public set pickValidationDate(value: string) {
        this._pickValidationDate = value;
    }

    public isValidString(teststring: string) {
        if (!teststring) { return false; }

        return (teststring.trim().length > 0);
    }

    onValidationDateChange() {
        this._dateValidationMessage = '';
        if (this.isValidString(this.pickValidationDate)) {
            if (!this.isValidDate(this.pickValidationDate)) {
                this._dateValidationMessage = 'The Validation Date value is not a valid date.';
                return;
            }
            const dateValue = this.pickValidationDate.replace(/-/, '/').replace(/-/, '/');
            this._edm.currentEntity.validationDate = new Date(dateValue);
        } else {
            this._edm.currentEntity.validationDate = null;
        }
    }

    isValidDate(dateString: string): boolean {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    }

    // ************************************************************************
    // * material search functions
    // ************************************************************************
    public async onSelectComponentMaterial() {
        UtilFns.showModal(this._materialSelectorComponent, this).then(mli => {
            if (mli == null) {
                return;
            }
            return this._uow.materialRepository.withId(mli.materialId);
        }).then(m => {
            if (m) {
                this._selectedMaterial = m;
                this.fetchMaterialComponent(m.materialId).then(() => {
                    if (this._selectedComponentMaterial == null)  {
                        this._selectedComponentMaterial             = this._uow.materialComponentFactory.create();
                        this._selectedComponentMaterial.materialId  = this._selectedMaterial.materialId;
                        this._selectedComponentMaterial.typeMaterialComponentClassId   = 1;

                        this._edmComponents.entities.push(this._selectedComponentMaterial);
                        this._edm.currentEntity.materialComponent = this._edmComponents.entities[0];
                    } else {
                        this._edm.currentEntity.materialComponent = this._selectedComponentMaterial;
                    }
                });
                this._componentMaterialChanged = true;
            }
        });
    }
}
